import {
  Component,
  OnInit,
  OnDestroy,
  Optional,
  Inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { environment } from '../../../../environments/environment';
import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JcdFactoryService } from '../../../global/services/jcd-factory.service';
import { GlobalService } from '../../../global/services/global.service';
import { ScreensService } from './screens.service';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-screen-edit',
    templateUrl: './screen-edit.component.html',
    styleUrls: ['./screen-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ScreenEditComponent implements OnInit, OnDestroy {
  title = 'Screen';
  minimise = false;
  loading = false;
  submitted = false;
  screen_data: any = {};
  mode: 'edit' | 'new' = 'new';
  crudLoading = false;

  screenStatus = [
    { name: 'Active', value: 'A' },
    { name: 'Locked', value: 'L' },
    { name: 'Maintenance', value: 'M' },
    { name: 'Removed', value: 'R' },
  ];

  screen_type = [
    { name: 'Grid Standard', value: 'grid' },
    { name: 'Grid Dynamic', value: 'grid-dynamic' },
    { name: 'Grid Batch Save', value: 'batch-save' },
    { name: 'Grid Import', value: 'grid-import' },
    { name: 'Grid Import Tab Delimited', value: 'grid-import-td' },
    { name: 'Dashboard', value: 'dashboard' },
    { name: 'Pivot', value: 'pivot' },
    { name: 'Grid Import Pivot', value: 'grid-import-pivot' },
    { name: 'Grid Standard Pivot', value: 'grid-standard-pivot' },
    { name: 'Form', value: 'form' },
    { name: 'Custom', value: 'custom' },
    { name: 'Grid Tax Placemat', value: 'grid-tax-placemat' },
    { name: 'Grid Import Validation', value: 'grid-import-validation' },
    { name: 'Custom With Tab', value: 'custom-tab' },
  ];

  filter_type = [
    { name: 'required', value: 'required' },
    { name: 'open', value: 'open' },
    { name: 'closed', value: 'closed' },
  ];

  filter_group: any[] = [];

  jcdlist: any = {
    channel: { list: [], selected: null, disabled: false },
    task: { list: [], taskList: [], selected: null, disabled: true },
    activity: { list: [], selected: null, disabled: true },
  };

  roles: { selected: any; list: any[]; loading: boolean; disabled: boolean } = {
    selected: null,
    list: [],
    loading: false,
    disabled: true,
  };

  // Batch process settings
  batch_settings = false;
  others_access: any = '';
  allow_reprocess: any = '';
  delete_upload: any = '';

  // Tags
  searchTagText = '';
  tagSearchResults: any[] = [];
  allTagsList: any[] = [];
  addedTags: any[] = [];
  tagBtnLoading = false;
  tagDropdownOpen = false;

  // Category & Tax Type
  categoryList: any[] = [];
  taxTypeList: any[] = [];
  dropdownLists: Record<'category' | 'taxType' | 'scenario', any[]> = {
    category: [],
    taxType: [],
    scenario: [],
  };
  selectedCategories: any[] = [];
  selectedTaxTypes: any[] = [];

  // Scenario Type
  scenarioTypeCode: any[] = [];
  selectedScenarioCodes: any[] = [];

  // Screens by activity (for refresh list)
  screensByActivityList: any[] = [];

  private rowData: any = {};
  private clientKey = '';
  private baseUrl = '';
  private adminApiUrl = '';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private screensService: ScreensService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private jcdFactory: JcdFactoryService,
    private globalService: GlobalService,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    @Optional() public dialogRef: MatDialogRef<ScreenEditComponent>,
  ) {}

  ngOnInit(): void {
    this.baseUrl = environment.base_url || '';
    this.adminApiUrl = environment.admin_api_url || this.baseUrl;
    this.clientKey = this.appService.USER_SETTINGS?.user?.client?.client_key ?? this.appService.USER_SETTINGS?.user?.client_key ?? '';

    this.rowData = this.dialogData?.rowData || {};
    this.mode = this.rowData?.SCREEN_KEY ? 'edit' : 'new';
    this.title = this.mode === 'edit' ? 'Edit Screen' : 'New Screen';

    this.init();
    this.initJcd();
    this.loadFilterGroup();
    this.loadScenarioTypeCodes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- Data loading ---

  private init(): void {
    if (this.mode === 'edit' && this.rowData?.SCREEN_KEY) {
      this.loadScreenData(this.rowData.VERSION_NUM);
    } else {
      this.screen_data = {
        allow_paste_xls: 1,
        allow_upload_xls: 1,
        allow_partial_save: 0,
        max_paste_rows: 2000,
        filter_inherit: 0,
        screen_priority: 1,
        enable_fetch: 0,
        enable_reprocess: 0,
        enable_delete: 0,
        display_issue_columns: 0,
        match_global_filter: 0,
        screen_template: 'app/components/grid/grid-standard-tpl.html',
        screen_controller: 'GirdStandardController as ctrl',
        screens_refresh_list: [],
      };
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private loadScreenData(versionNum?: number): void {
    this.loading = true;
    this.cdr.detectChanges();

    const globalParams = this.globalService.global?.globalParams || {};

    this.screensService
      .loadScreen(
        this.baseUrl,
        this.rowData.SCREEN_KEY,
        this.rowData.CLIENT_KEY ?? this.clientKey,
        versionNum,
        globalParams
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.loading = false;
          if (data?.callSuccess === '1' && data?.jsonObject?.[0]) {
            const raw = data.jsonObject[0];
            this.screen_data = Object.keys(raw).reduce((acc: any, k) => {
              acc[k.toLowerCase()] = raw[k];
              return acc;
            }, {});

            this.screen_data.screen_safe_link = this.screen_data.screen_link
              ? this.screen_data.screen_link.replace('workspace.', '')
              : '';

            ['version_num', 'min_version_num', 'max_version_num'].forEach(k => {
              if (this.screen_data[k] != null) {
                this.screen_data[k] = Number(this.screen_data[k]);
              }
            });

            if (this.screen_data.enable_fetch || this.screen_data.enable_reprocess || this.screen_data.enable_delete) {
              this.batch_settings = true;
            }

            this.applyJcdFromScreenData();
            this.loadRoles(this.screen_data.jcd_ta_key, this.screen_data.jcd_ta_role_key);
            this.loadScreenTags();
            this.loadTaxCategories(this.screen_data.screens_tax_types_list, this.screen_data.screens_category_list);
            this.applySelectedScenarioCodes();
          } else if (data?.errorMessage) {
            this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.loading = false;
          this.gtwSnackbarService.openSnackBar(err?.message || 'Error loading screen', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  private loadFilterGroup(): void {
    this.screensService.loadFilterGroup(this.baseUrl, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (data?.callSuccess === '1' && Array.isArray(data?.jsonObject)) {
            const arr = data.jsonObject.map((o: any) =>
              Object.keys(o).reduce((acc: any, k) => {
                acc[k.toLowerCase()] = o[k];
                return acc;
              }, {})
            );
            this.filter_group = [{ filter_group_name: 'none', filter_group_key: '' }, ...arr];
          }
          this.cdr.detectChanges();
        },
      });
  }

  // --- JCD integration ---

  private initJcd(): void {
    const applyJcdLists = () => {
      if (this.mode === 'edit' && this.screen_data?.jcd_ta_key) {
        this.buildJcdLists(this.screen_data.jcd_ta_key);
      } else {
        this.buildJcdLists(null);
      }
      this.cdr.detectChanges();
    };

    if (this.jcdFactory.JCDFactory.JCDList?.length > 0) {
      applyJcdLists();
    } else {
      this.jcdFactory.loadJCDObj().then(() => applyJcdLists());
    }
  }

  private applyJcdFromScreenData(): void {
    if (this.jcdFactory.JCDFactory.JCDList?.length > 0) {
      this.buildJcdLists(this.screen_data.jcd_ta_key || null);
      this.cdr.detectChanges();
    }
  }

  private buildJcdLists(jcdTaKey: any): void {
    const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
    let selectedChannel: any = null;
    let selectedTask: any = null;
    let selectedActivity: any = null;

    if (jcdTaKey) {
      const match = jcdList.find((i: any) => i.JCD_TA_KEY == jcdTaKey);
      if (match) {
        selectedChannel = match.CHANNEL_KEY;
        selectedTask = match.TASK_KEY;
        selectedActivity = match.ACTIVITY_KEY;
      }
    }

    const channelMap = new Map<number, any>();
    jcdList.forEach((v: any) => {
      if (!channelMap.has(v.CHANNEL_KEY)) {
        channelMap.set(v.CHANNEL_KEY, { name: v.CHANNEL_NAME, value: v.CHANNEL_KEY });
      }
    });
    const channelList = Array.from(channelMap.values());
    const channelSelected = selectedChannel ? channelList.find(c => c.value === selectedChannel) || null : null;

    this.jcdlist = {
      channel: { list: channelList, selected: channelSelected, disabled: false },
      task: { list: [], taskList: [], selected: null, disabled: !channelSelected },
      activity: { list: [], selected: null, disabled: true },
    };

    if (channelSelected) {
      this.buildTaskList(channelSelected.value, selectedTask, selectedActivity);
    }
  }

  private buildTaskList(channelKey: any, selectedTaskKey: any, selectedActivityKey: any): void {
    const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
    const taskMap = new Map<number, any>();
    jcdList.forEach((v: any) => {
      if (v.CHANNEL_KEY == channelKey && !taskMap.has(v.TASK_KEY)) {
        taskMap.set(v.TASK_KEY, {
          name: v.TASK_NAME, value: v.TASK_KEY,
          parent_task_key: v.PARENT_TASK_KEY, level: v.level, indent: v.indent,
        });
      }
    });
    const taskList = Array.from(taskMap.values());
    const taskListTree = this.jcdFactory.addIndent(safeJsonInput<any>(JSON.stringify(taskList), null, 'screen-edit.parse1'));

    this.jcdlist.task = {
      list: taskList, taskList: taskListTree,
      selected: selectedTaskKey || null, disabled: false,
    };

    if (selectedTaskKey) {
      this.buildActivityList(channelKey, selectedTaskKey, selectedActivityKey);
    } else {
      this.jcdlist.activity = { list: [], selected: null, disabled: true };
    }
  }

  private buildActivityList(channelKey: any, taskKey: any, selectedActivityKey: any): void {
    const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
    const actMap = new Map<number, any>();
    jcdList.forEach((v: any) => {
      if (v.CHANNEL_KEY == channelKey && v.TASK_KEY == taskKey && !actMap.has(v.ACTIVITY_KEY)) {
        actMap.set(v.ACTIVITY_KEY, { name: v.ACTIVITY_NAME, value: v.ACTIVITY_KEY });
      }
    });
    const actList = Array.from(actMap.values());
    const actSelected = selectedActivityKey ? actList.find(a => a.value === selectedActivityKey) || null : null;

    this.jcdlist.activity = { list: actList, selected: actSelected, disabled: false };

    if (actSelected) {
      const match = jcdList.find((i: any) =>
        i.CHANNEL_KEY == channelKey && i.TASK_KEY == taskKey && i.ACTIVITY_KEY == actSelected.value
      );
      this.screen_data.jcd_ta_key = match?.JCD_TA_KEY || null;
    }
  }

  onJcdSelectChange(num: number): void {
    if (num <= 3) {
      const chanVal = this.jcdlist.channel?.selected?.value || null;
      this.jcdlist.task = { list: [], taskList: [], selected: null, disabled: !chanVal };
      this.jcdlist.activity = { list: [], selected: null, disabled: true };
      this.screen_data.jcd_ta_key = null;
      if (chanVal) {
        this.buildTaskList(chanVal, null, null);
      }
    } else if (num === 4) {
      const chanVal = this.jcdlist.channel?.selected?.value || null;
      const taskVal = this.jcdlist.task?.selected || null;
      this.jcdlist.activity = { list: [], selected: null, disabled: true };
      this.screen_data.jcd_ta_key = null;
      if (chanVal && taskVal) {
        this.buildActivityList(chanVal, taskVal, null);
      }
    } else if (num === 5) {
      const chanVal = this.jcdlist.channel?.selected?.value || null;
      const taskVal = this.jcdlist.task?.selected || null;
      const actVal = this.jcdlist.activity?.selected?.value || null;
      this.screen_data.jcd_ta_key = null;
      if (chanVal && taskVal && actVal) {
        const jcdList: any[] = this.jcdFactory.JCDFactory.JCDList || [];
        const match = jcdList.find((i: any) =>
          i.CHANNEL_KEY == chanVal && i.TASK_KEY == taskVal && i.ACTIVITY_KEY == actVal
        );
        this.screen_data.jcd_ta_key = match?.JCD_TA_KEY || null;
      }
    }

    this.loadRoles(this.screen_data.jcd_ta_key, null);
    this.cdr.detectChanges();
  }

  // --- Roles ---

  private loadRoles(jcdTaKey: any, roleKey: any): void {
    if (!jcdTaKey) {
      this.roles = { selected: null, list: [], loading: false, disabled: true };
      return;
    }
    this.roles.loading = true;
    this.cdr.detectChanges();

    this.screensService.loadRoles(this.baseUrl, jcdTaKey, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.roles.loading = false;
          this.roles.disabled = false;
          if (data?.callSuccess === '1' && data?.jsonObject) {
            this.roles.list = data.jsonObject.map((item: any) => {
              const lower: any = {};
              Object.keys(item).forEach(k => lower[k.toLowerCase()] = item[k]);
              return lower;
            });
            if (roleKey) {
              this.roles.selected = this.roles.list.find((r: any) => r.jcd_ta_role_key === roleKey) || null;
              this.others_access = this.roles.list.find((r: any) => r.jcd_ta_role_key === this.screen_data?.fetch_role_key) || '';
              this.allow_reprocess = this.roles.list.find((r: any) => r.jcd_ta_role_key === this.screen_data?.reprocess_role_key) || '';
              this.delete_upload = this.roles.list.find((r: any) => r.jcd_ta_role_key === this.screen_data?.delete_role_key) || '';
            }
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.roles.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  // --- Version navigation ---

  updateVersion(versionNum: number): void {
    const ver = Number(versionNum);
    if (!isNaN(ver)) {
      this.loadScreenData(ver);
    }
  }

  get canGoPrevious(): boolean {
    const min = Number(this.screen_data?.min_version_num);
    const current = Number(this.screen_data?.version_num);
    return this.mode === 'edit' && !isNaN(min) && !isNaN(current) && min <= current - 1;
  }

  get canGoNext(): boolean {
    const max = Number(this.screen_data?.max_version_num);
    const current = Number(this.screen_data?.version_num);
    return this.mode === 'edit' && !isNaN(max) && !isNaN(current) && max >= current + 1;
  }

  // --- Screen link ---

  createScreenLink(val: string): void {
    this.screen_data.screen_safe_link = val
      ? val.replace(/[^0-9A-Za-z-_]/g, '-').toLowerCase()
      : '';
    this.cdr.detectChanges();
  }

  // --- API key generation ---

  generateApiKey(): void {
    const st = this.screen_data.screen_type;
    const jsonURL =
      st === 'grid' ? 'loadGridJson' :
      st === 'grid-dynamic' ? 'loadDynamicGridJson' :
      st === 'pivot' ? 'loadPivotGridJson' :
      st === 'grid-import' ? 'emptyGridJson' :
      st === 'grid-import-td' ? 'emptyGridJson' :
      st === 'grid-import-validation' ? 'emptryGridJson' :
      st === 'grid-import-pivot' ? 'emptyGridJson' :
      st === 'grid-standard-pivot' ? 'loadGridJsonPivotWithoutHeaders' :
      st === 'custom' ? '' :
      st === 'form' ? '' :
      st === 'dashboard' ? 'getDashboardByKey' :
      st === 'custom-tab' ? '' : 'loadGridJson';

    const api: any = { get: '' };
    if (this.screen_data.grid_key) {
      api.get = jsonURL + '?grid_id=' + this.screen_data.grid_key;
    }

    if (st !== 'batch-save' && st !== 'grid-import' && st !== 'grid-import-td'
      && st !== 'grid-import-validation' && st !== 'grid-import-pivot') {
      this.screen_data.batch_save_action_key = '';
    }
    if (st === 'grid-import' || st === 'grid-import-validation' || st === 'grid-import-td') {
      this.screen_data.action_key = '';
    }

    if (this.screen_data.dashboard_id) {
      api.get = jsonURL + '?dashboard_id=' + this.screen_data.dashboard_id;
    }
    if (this.screen_data.action_key) {
      api.get = api.get + '&action_id=' + this.screen_data.action_key;
    }

    if (this.screen_data.batch_save_action_key && st === 'batch-save') {
      api['save'] = 'batchSave?action_id=' + this.screen_data.batch_save_action_key;
    } else if (this.screen_data.batch_save_action_key) {
      api['save'] = 'gridBatchImportValidation?action_id=' + this.screen_data.batch_save_action_key;
    }

    this.screen_data.api = JSON.stringify(api);

    if (st === 'dashboard') {
      this.screen_data.screen_controller = 'StandardDashboardController as ctrl';
      this.screen_data.screen_template = 'app/components/dashboardExecutive/executive-dashboards.html';
    } else if (st === 'custom-tab') {
      this.screen_data.screen_controller = '';
      this.screen_data.screen_template = '';
    } else {
      this.screen_data.screen_controller = 'GirdStandardController as ctrl';
      this.screen_data.screen_template = 'app/components/grid/grid-standard-tpl.html';
    }
  }

  // --- Conditional visibility helpers ---

  get showActionKey(): boolean {
    const st = this.screen_data.screen_type;
    return st === 'grid' || st === 'pivot' || st === 'batch-save' || st === 'grid-standard-pivot' || st === 'grid-dynamic';
  }

  get showGridKey(): boolean {
    const st = this.screen_data.screen_type;
    return st !== 'custom' && st !== 'form' && st !== 'dashboard' && st !== 'custom-tab';
  }

  get showSaveActionKey(): boolean {
    const st = this.screen_data.screen_type;
    return st === 'batch-save' || st === 'grid-import' || st === 'grid-import-validation' || st === 'grid-import-td' || st === 'grid-import-pivot';
  }

  get showDashboardId(): boolean {
    return this.screen_data.screen_type === 'dashboard';
  }

  get showApiCalls(): boolean {
    const st = this.screen_data.screen_type;
    return st !== 'custom' && st !== 'form' && st !== 'custom-tab';
  }

  get apiCallsColClass(): string {
    let usedCols = 2;
    if (this.showActionKey) usedCols += 1;
    if (this.showGridKey) usedCols += 1;
    if (this.showSaveActionKey) usedCols += 1;
    if (this.showDashboardId) usedCols += 1;
    return 'col-md-' + (12 - usedCols);
  }

  get showFilterSection(): boolean {
    const st = this.screen_data.screen_type;
    return st === 'grid' || st === 'custom' || st === 'dashboard' || st === 'pivot'
      || st === 'batch-save' || st === 'grid-import' || st === 'grid-import-td'
      || st === 'grid-standard-pivot' || st === 'grid-import-pivot' || st === 'grid-dynamic';
  }

  get isImportType(): boolean {
    const st = this.screen_data.screen_type;
    return st === 'grid-import' || st === 'grid-import-td' || st === 'grid-import-validation' || st === 'grid-import-pivot';
  }

  get isImportWithPaste(): boolean {
    const st = this.screen_data.screen_type;
    return st === 'grid-import' || st === 'grid-import-validation' || st === 'grid-import-pivot';
  }

  get showDisplayIssueColumns(): boolean {
    const st = this.screen_data.screen_type;
    return st !== 'custom' && st !== 'custom-tab' && st !== 'dashboard';
  }

  // --- Batch process settings ---

  clearValues(name: string, value: any): void {
    if (name === 'all' && !value) {
      this.screen_data.enable_fetch = 0;
      this.others_access = '';
      this.screen_data.enable_reprocess = 0;
      this.allow_reprocess = '';
      this.screen_data.enable_delete = 0;
      this.delete_upload = '';
      this.screen_data.delete_action_key = '';
    } else if (name === 'others' && !value) {
      this.others_access = '';
    } else if (name === 'reprocess' && !value) {
      this.allow_reprocess = '';
    } else if (name === 'delete' && !value) {
      this.delete_upload = '';
      this.screen_data.delete_action_key = '';
    }
  }

  // --- Tags ---

  private loadScreenTags(): void {
    if (!this.screen_data?.screen_key) return;
    this.screensService.getScreenTagging(this.adminApiUrl, this.screen_data.screen_key, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (data?.callSuccess == 1) {
            this.allTagsList = this.arrObjToLower(data.tags_list || []);
            this.tagSearchResults = [...this.allTagsList];
            this.addedTags = this.arrObjToLower(data.selected_tags_list || []);
          }
          this.cdr.detectChanges();
        },
      });
  }

  tagSearch(text: string): void {
    this.searchTagText = text.toLowerCase();
    this.tagDropdownOpen = true;
    if (this.searchTagText && this.searchTagText.length > 0) {
      this.tagSearchResults = this.allTagsList.filter(
        (tag: any) => tag.tag_name?.toLowerCase().indexOf(this.searchTagText) !== -1
      );
    } else {
      this.tagSearchResults = [...this.allTagsList];
    }
  }

  selectTagItem(tag: any): void {
    this.searchTagText = tag.tag_name;
    this.tagDropdownOpen = false;
    this.cdr.detectChanges();
  }

  closeTagDropdownDelayed(): void {
    setTimeout(() => {
      this.tagDropdownOpen = false;
      this.cdr.detectChanges();
    }, 200);
  }

  addTags(inputTagText: string): void {
    if (!inputTagText) return;
    const hasSame = this.addedTags.some((t: any) => t.tag_name === inputTagText);
    if (hasSame) {
      this.gtwSnackbarService.openSnackBar('Tag already exists', 'gtw-snackbar--yellow');
      return;
    }

    this.tagBtnLoading = true;
    this.cdr.detectChanges();

    const tagsToSave = [...this.addedTags, { tag_name: inputTagText, tag_desc: '' }];

    this.screensService.saveScreenTags(this.baseUrl, this.screen_data.screen_key, tagsToSave, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.tagBtnLoading = false;
          if (data?.callSuccess == 1) {
            this.searchTagText = '';
            this.gtwSnackbarService.openSnackBar('Tag saved successfully', 'gtw-snackbar--green');
            this.loadScreenTags();
          } else {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error saving tag', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.tagBtnLoading = false;
          this.gtwSnackbarService.openSnackBar('Error saving tag', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  deleteTags(index: number, tag: any): void {
    this.addedTags.splice(index, 1);
    const removeTags = [{ tag_key: tag.tag_key, tag_name: tag.tag_name, status: 0 }];
    this.searchTagText = '';

    this.screensService.saveScreenTags(this.baseUrl, this.screen_data.screen_key, removeTags, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (data?.callSuccess == 1) {
            this.gtwSnackbarService.openSnackBar('Tag removed successfully', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error removing tag', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
      });
  }

  // --- Category & Tax Type ---

  private loadTaxCategories(selectedTaxList?: string, selectedCategoryList?: string): void {
    this.screensService.getScreenTaxCategories(this.adminApiUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (data?.callSuccess === '1' || data?.callSuccess == 1) {
            if (data.tax_types_list) {
              this.taxTypeList = this.toAppkitOptions(
                data.tax_types_list.map((i: any) => ({ label: i.label, value: i.id })),
              );
              this.dropdownLists.taxType = [...this.taxTypeList];
              if (selectedTaxList) {
                const selectedIds = selectedTaxList.split(',');
                this.selectedTaxTypes = this.taxTypeList.filter((t: any) => selectedIds.indexOf(String(t.value)) > -1);
              }
            }
            if (data.categories_list) {
              this.categoryList = this.toAppkitOptions(
                data.categories_list.map((i: any) => ({ label: i.label, value: i.id })),
              );
              this.dropdownLists.category = [...this.categoryList];
              if (selectedCategoryList) {
                const selectedIds = selectedCategoryList.split(',');
                this.selectedCategories = this.categoryList.filter((c: any) => selectedIds.indexOf(String(c.value)) > -1);
              }
            }
          }
          this.cdr.detectChanges();
        },
      });
  }

  // --- Scenario Type ---

  private loadScenarioTypeCodes(): void {
    this.screensService.getScenarioTypeCodes(this.baseUrl)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.scenarioTypeCode = this.toAppkitOptions(
            Array.isArray(data) ? data : (data?.data || []),
          );
          this.dropdownLists.scenario = [...this.scenarioTypeCode];
          this.applySelectedScenarioCodes();
          this.cdr.detectChanges();
        },
      });
  }

  private applySelectedScenarioCodes(): void {
    if (this.screen_data?.scenario_type_codes && this.scenarioTypeCode.length > 0) {
      const codes = this.screen_data.scenario_type_codes.split(',');
      this.selectedScenarioCodes = this.scenarioTypeCode.filter(
        (i: any) => codes.indexOf(i.scenario_type_code) > -1
      );
    }
  }

  onDropdownFilterResult(
    key: 'category' | 'taxType' | 'scenario',
    event: any,
  ): void {
    this.dropdownLists[key] = event?.result || [];
  }

  private toAppkitOptions(items: any[]): any[] {
    return (items || []).map((item: any) => ({
      ...item,
      showCheckbox: true,
      role: 'checkbox',
    }));
  }

  // --- Helpers ---

  private arrObjToLower(arr: any[]): any[] {
    if (!Array.isArray(arr)) return [];
    return arr.map((o: any) =>
      Object.keys(o).reduce((acc: any, k) => {
        acc[k.toLowerCase()] = o[k];
        return acc;
      }, {})
    );
  }

  private isJsonString(str: string): boolean {
    try { safeJsonInput<any>(str, null, 'screen-edit.parse2'); return true; } catch { return false; }
  }

  // --- Save / Cancel ---

  cancel(): void {
    this.dialogRef?.close();
  }

  save(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    if (this.screen_data.api && this.screen_data.api.length && !this.isJsonString(this.screen_data.api)) {
      this.gtwSnackbarService.openSnackBar("The field 'API Calls' doesn't contain valid JSON", 'gtw-snackbar--red');
      return;
    }

    if (!this.screen_data?.screen_name?.trim()) {
      this.submitted = true;
      this.gtwSnackbarService.openSnackBar('Please correct the errors below', 'gtw-snackbar--red');
      this.cdr.detectChanges();
      return;
    }

    this.crudLoading = true;
    this.cdr.detectChanges();

    this.screen_data.client_key = this.clientKey;

    if (Array.isArray(this.screen_data.screens_refresh_list)) {
      this.screen_data.screens_refresh_list = this.screen_data.screens_refresh_list.join(',');
    } else if (this.screen_data.screens_refresh_list == null) {
      this.screen_data.screens_refresh_list = '';
    }

    if (this.roles.selected) {
      this.screen_data.jcd_ta_role_key = this.roles.selected.jcd_ta_role_key;
    }

    this.screen_data.screen_url = '/' + this.screen_data.screen_safe_link;
    this.screen_data.fetch_role_key = this.others_access ? this.others_access.jcd_ta_role_key : null;
    this.screen_data.reprocess_role_key = this.allow_reprocess ? this.allow_reprocess.jcd_ta_role_key : null;
    this.screen_data.delete_role_key = this.delete_upload ? this.delete_upload.jcd_ta_role_key : null;

    if (this.selectedScenarioCodes.length === 0) {
      this.screen_data.scenarioTypeCodes = 'RAF';
    } else {
      this.screen_data.scenarioTypeCodes = this.selectedScenarioCodes.map((i: any) => i.scenario_type_code).join(',');
    }

    this.screen_data.placematid = this.screen_data.placemat_id ?? '';

    if (this.screen_data.screen_type !== 'custom') {
      this.screen_data.screen_link = 'workspace.' + this.screen_data.screen_safe_link;
    } else {
      this.screen_data.screen_link = this.screen_data.screen_safe_link;
    }

    this.screen_data.screens_tax_types_list = this.selectedTaxTypes.map((t: any) => t.value).join(',');
    this.screen_data.screens_category_list = this.selectedCategories.map((c: any) => c.value).join(',');

    const activityKey = this.jcdlist.activity?.selected?.value ?? '';

    const payload: any = {
      screen_key: this.screen_data.screen_key ?? '',
      activity_key: activityKey,
      json_string: JSON.stringify(this.screen_data),
    };

    this.screensService.saveScreen(this.adminApiUrl, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.crudLoading = false;
          this.cdr.detectChanges();
          if (data?.callSuccess === '0' || data?.callSuccess == 0) {
            this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Save failed', 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('Screen has been saved', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          }
        },
        error: (err) => {
          this.crudLoading = false;
          this.cdr.detectChanges();
          this.gtwSnackbarService.openSnackBar(err?.message || 'Save failed', 'gtw-snackbar--red');
        },
      });
  }
}
